import { ITranscript, ParagraphData } from '@insights_module/types/Episode';
import artworkFallback from '@assets/images/artwork-fallback.svg';
import { EpisodeDetails, Show } from '@app/types/ADCTypes';

/**
 * Parse the transcript into array of paragraphs data, containing the words, start time, end time, and paragraph string.
 * @param {ITranscript[]} transcript The transcript to parse.
 * @returns {ParagraphData[]} An array of paragraphs data.
 */
export function getParagraphsDataForSegment(transcript: ITranscript[]): ParagraphData[] {
  const emptyParagraph: ParagraphData = {
    paragraphWords: [],
    startTime: '',
    endTime: '',
    paragraphString: '',
  };
  const END_OF_PARAGRAPH = ['.', '!', '?', ';'];

  return transcript.reduce(
    (paragraphs, word, index) => {
      const { alternatives, start_time, type } = word;
      const lastParagraph = paragraphs[paragraphs.length - 1];

      if (type === 'punctuation' && END_OF_PARAGRAPH.includes(alternatives[0].content)) {
        lastParagraph.paragraphString += alternatives[0].content;
        lastParagraph.endTime =
          lastParagraph.paragraphWords[lastParagraph.paragraphWords.length - 1].end_time;

        if (index !== transcript.length - 1) {
          paragraphs.push({ ...emptyParagraph, paragraphWords: [] });
        }

        return paragraphs;
      }

      if (lastParagraph.paragraphWords.length === 0) {
        lastParagraph.startTime = start_time;
        lastParagraph.paragraphString += alternatives[0].content;
        lastParagraph.paragraphWords.push(word);

        return paragraphs;
      }

      if (type === 'punctuation') {
        lastParagraph.paragraphString += alternatives[0].content;

        return paragraphs;
      }

      if (type === 'pronunciation' && index === transcript.length - 1) {
        lastParagraph.endTime = word.end_time;
      }

      lastParagraph.paragraphString += ` ${alternatives[0].content}`;
      lastParagraph.paragraphWords.push(word);

      return paragraphs;
    },
    [{ ...emptyParagraph, paragraphWords: [] }] as ParagraphData[]
  );
}

export const validateArtworkUrl = (str: string | undefined | null): string => {
  const regex = /(http(s?):)([/|.|\w|\s|-])*\.(?:png|gif|webp|jpeg|jpg|gif)/;

  if (str && regex.test(str)) {
    return str;
  }

  return artworkFallback;
};

function removeSubstrings(str: string, subStr: string): string {
  const regex = new RegExp(`${subStr.trim()}`, 'gi');
  return str.replaceAll(regex, '');
}

/**
 * Remove all Show titles from episode title
 * @param {string} episodeTitle - The episode title
 * @param {string} showTitle - The show title
 * @returns {string} - The episode title without show title
 */

export function removeShowTitleFromEpisodeTitle(episodeTitle: string, showTitle: string): string {
  try {
    let _episodeTitle = removeSubstrings(episodeTitle, showTitle).trim();
    while (_episodeTitle.startsWith('-')) {
      _episodeTitle = _episodeTitle.slice(1).trim();
    }
    return _episodeTitle;
  } catch (error) {
    return episodeTitle;
  }
}

/**
 * Get the latest group data from episode details
 * @param {EpisodeDetails} episodeDetails
 * @returns {Show | null}
 */

export function getLatestGroupDataFromEpisodeDetails(episodeDetails: EpisodeDetails): Show | null {
  if (!episodeDetails || (episodeDetails?.groups && !episodeDetails?.groups.length)) {
    return null;
  }

  const showGroups = episodeDetails.groups.filter((group) => group.type === 'show');
  if (!showGroups.length) {
    return null;
  }

  if (showGroups.length === 1) {
    return showGroups[0];
  }

  return showGroups.reduce((latestShow, currentShow) => {
    if (new Date(currentShow.created_at) > new Date(latestShow.created_at)) {
      return currentShow;
    }
    return latestShow;
  }, showGroups[0]);
}
